import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { catchError, map, mergeMap, Observable, of, switchMap, tap } from 'rxjs';

import { DossiersService } from '../services/dossiers.service';

import {
  addInvolved,
  addInvolvedFailure,
  addInvolvedSuccess,
  addLink,
  addLinkFailure,
  addLinkSuccess,
  addRelation,
  addRelationFailure,
  addRelationSuccess,
  fetchAvailableStatuses,
  fetchAvailableStatusesFailure,
  fetchAvailableStatusesSuccess,
  fetchDossier,
  fetchDossierData,
  fetchDossierDataFailure,
  fetchDossierDataSuccess,
  fetchDossierFailure,
  fetchDossierSuccess,
  removeDocument,
  removeDocumentFailure,
  removeDocumentSuccess,
  removeInvolved,
  removeInvolvedFailure,
  removeInvolvedSuccess,
  removeLink,
  removeLinkFailure,
  removeLinkSuccess,
  removeRelation,
  removeRelationFailure,
  removeRelationSuccess,
  updateDossierHeader,
  updateDossierHeaderFailure,
  updateDossierHeaderSuccess
} from './dossiers.actions';

@Injectable()
export class DossierEffects {
  constructor(
    private readonly router: Router,
    private readonly actions$: Actions,
    private readonly dossiersService: DossiersService,
    private readonly messageService: MessageService,
    private readonly translateService: TranslateService
  ) {}

  fetchDossier$ = createEffect(
    (): Observable<Action> =>
      this.actions$.pipe(
        ofType(fetchDossier),
        switchMap((action) =>
          this.dossiersService.fetchDossier(action.id).pipe(
            map((data) => fetchDossierSuccess({ data })),
            catchError((error: HttpErrorResponse) => of(fetchDossierFailure({ id: action.id, error })))
          )
        )
      )
  );

  fetchDossierData$ = createEffect(
    (): Observable<Action> =>
      this.actions$.pipe(
        ofType(fetchDossierData),
        mergeMap((action) =>
          this.dossiersService.fetchDossierData(action.id, action.dataKey, action.context).pipe(
            map((data) => fetchDossierDataSuccess({ ...action, data: data, context: action.context })),
            catchError((error: any) => of(fetchDossierDataFailure({ error })))
          )
        )
      )
  );

  updateDossierHeader$ = createEffect(
    (): Observable<Action> =>
      this.actions$.pipe(
        ofType(updateDossierHeader),
        mergeMap((action) =>
          this.dossiersService.updateDossierHeader(action.data).pipe(
            map((data) => updateDossierHeaderSuccess({ data })),
            catchError((error: any) => of(updateDossierHeaderFailure({ error })))
          )
        )
      )
  );

  updateDossierHeaderSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(updateDossierHeaderSuccess),
        tap(() => {
          this.messageService.add({
            severity: 'success',
            detail: this.translateService.instant('dossier.update.success'),
            life: 3000
          });
        })
      ),
    { dispatch: false }
  );

  updateDossierHeaderFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(updateDossierHeaderFailure),
        tap(() => {
          this.messageService.add({
            severity: 'error',
            detail: this.translateService.instant('dossier.update.failure'),
            life: 3000
          });
        })
      ),
    { dispatch: false }
  );

  fetchAvailableStatuses$ = createEffect(
    (): Observable<Action> =>
      this.actions$.pipe(
        ofType(fetchAvailableStatuses),
        switchMap(() =>
          this.dossiersService.fetchAvailableStatuses().pipe(
            map((data) => fetchAvailableStatusesSuccess({ data })),
            catchError((error: any) => of(fetchAvailableStatusesFailure({ error })))
          )
        )
      )
  );

  removeDocument$ = createEffect(
    (): Observable<Action> =>
      this.actions$.pipe(
        ofType(removeDocument),
        mergeMap((action) =>
          this.dossiersService.removeDocument(action.dossierId, action.document.documentId).pipe(
            tap(() => {
              this.messageService.add({
                severity: 'success',
                detail: this.translateService.instant('dossier.document.remove.success', {
                  '0': action.document.name
                }),
                life: 3000
              });
            }),
            map(() => removeDocumentSuccess({ ...action, documentId: action.document.documentId })),
            catchError((error: any) => {
              this.messageService.add({
                severity: 'error',
                detail: this.translateService.instant('dossier.document.remove.failed', {
                  '0': action.document.name
                }),
                life: 3000
              });
              return of(removeDocumentFailure({ error }));
            })
          )
        )
      )
  );

  addRelation$ = createEffect(
    (): Observable<Action> =>
      this.actions$.pipe(
        ofType(addRelation),
        mergeMap((action) =>
          this.dossiersService.addRelation(action.dossierId, action.context, action.relation.id).pipe(
            tap(() => {
              this.messageService.add({
                severity: 'success',
                detail: this.translateService.instant('dossier.relation.add.success', {
                  '0': this.translateService.instant('common.type.' + action.context.toLowerCase()),
                  '1': action.relation.title
                }),
                life: 3000
              });
            }),
            map((relation) =>
              addRelationSuccess({
                ...action,
                relation: relation
              })
            ),
            catchError((error: any) => {
              this.messageService.add({
                severity: 'error',
                detail: this.translateService.instant('dossier.relation.add.failed', {
                  '0': this.translateService.instant('common.type.' + action.context.toLowerCase()),
                  '1': action.relation.title
                }),
                life: 3000
              });
              return of(addRelationFailure({ error }));
            })
          )
        )
      )
  );

  removeRelation$ = createEffect(
    (): Observable<Action> =>
      this.actions$.pipe(
        ofType(removeRelation),
        mergeMap((action) =>
          this.dossiersService.removeRelation(action.dossierId, action.context, action.relation.id).pipe(
            tap(() => {
              this.messageService.add({
                severity: 'success',
                detail: this.translateService.instant('dossier.relation.remove.success', {
                  '0': this.translateService.instant('common.type.' + action.context.toLowerCase()),
                  '1': action.relation.subject
                }),
                life: 3000
              });
            }),
            map(() => removeRelationSuccess({ ...action, relationId: action.relation.id })),
            catchError((error: any) => {
              this.messageService.add({
                severity: 'error',
                detail: this.translateService.instant('dossier.relation.remove.failed', {
                  '0': this.translateService.instant('common.type.' + action.context.toLowerCase()),
                  '1': action.relation.subject
                }),
                life: 3000
              });
              return of(removeRelationFailure({ error }));
            })
          )
        )
      )
  );

  addLink$ = createEffect(
    (): Observable<Action> =>
      this.actions$.pipe(
        ofType(addLink),
        mergeMap((action) =>
          this.dossiersService.addLink(action.dossierId, action.link.id).pipe(
            tap(() => {
              this.messageService.add({
                severity: 'success',
                detail: this.translateService.instant('dossier.link.add.success', {
                  '0': action.link.title
                }),
                life: 3000
              });
            }),
            map((link) => addLinkSuccess({ ...action, link })),
            catchError((error: any) => {
              this.messageService.add({
                severity: 'error',
                detail: this.translateService.instant('dossier.link.add.failed', {
                  '0': action.link.title
                }),
                life: 3000
              });
              return of(addLinkFailure({ error }));
            })
          )
        )
      )
  );

  removeLink$ = createEffect(
    (): Observable<Action> =>
      this.actions$.pipe(
        ofType(removeLink),
        mergeMap((action) =>
          this.dossiersService.removeLink(action.dossierId, action.link.id).pipe(
            tap(() => {
              this.messageService.add({
                severity: 'success',
                detail: this.translateService.instant('dossier.link.remove.success', {
                  '0': action.link.subject
                }),
                life: 3000
              });
            }),
            map(() => removeLinkSuccess({ ...action, linkId: action.link.id })),
            catchError((error: any) => {
              this.messageService.add({
                severity: 'error',
                detail: this.translateService.instant('dossier.link.remove.failed', {
                  '0': action.link.subject
                }),
                life: 3000
              });
              return of(removeLinkFailure({ error }));
            })
          )
        )
      )
  );

  addInvolved$ = createEffect(
    (): Observable<Action> =>
      this.actions$.pipe(
        ofType(addInvolved),
        mergeMap((action) =>
          this.dossiersService.addInvolved(action.dossierId, action.context, action.involved.key).pipe(
            tap(() => {
              this.messageService.add({
                severity: 'success',
                detail: this.translateService.instant('dossier.involved.add.success', {
                  '0': action.involved.label
                }),
                life: 3000
              });
            }),
            map((item) =>
              addInvolvedSuccess({
                ...action,
                involved: {
                  ...item,
                  removable: true
                }
              })
            ),
            catchError((error: any) => {
              this.messageService.add({
                severity: 'error',
                detail: this.translateService.instant('dossier.involved.add.failed', {
                  '0': action.involved.label
                }),
                life: 3000
              });
              return of(addInvolvedFailure({ error }));
            })
          )
        )
      )
  );

  removeInvolved$ = createEffect(
    (): Observable<Action> =>
      this.actions$.pipe(
        ofType(removeInvolved),
        mergeMap((action) =>
          this.dossiersService.removeInvolved(action.dossierId, action.context, action.involved.key).pipe(
            tap(() => {
              this.messageService.add({
                severity: 'success',
                detail: this.translateService.instant('dossier.involved.remove.success', {
                  '0': action.involved.label
                }),
                life: 3000
              });
            }),
            map(() => removeInvolvedSuccess({ ...action, involvedId: action.involved.key })),
            catchError((error: any) => {
              this.messageService.add({
                severity: 'error',
                detail: this.translateService.instant('dossier.involved.remove.failed', {
                  '0': action.involved.label
                }),
                life: 3000
              });
              return of(removeInvolvedFailure({ error }));
            })
          )
        )
      )
  );

  refreshstatus$ = createEffect(
    (): Observable<Action> =>
      this.actions$.pipe(
        ofType(addRelationSuccess),
        map((action) => fetchDossier({ id: action.dossierId, showLoading: false }))
      )
  );
}
